import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

import { prohibitedWords } from 'src/constants/prohibitedWords'
import { useAuthState, useAuthDispatch, authActionTypes } from 'src/providers/AuthProviders'
import patchUser from 'src/services/patch/patchUser'

import AvatarGrid from 'src/components/house/AvatarGrid'
import ButtonAvatarRepo from 'src/components/house/ButtonAvatarRepo'
import HousePublicButton from 'src/components/house/HousePublicButton'
import LabelModalHouse from 'src/components/house/LabelModalHouse'
import LabelRank from 'src/components/house/LabelRank'
import ModalHouseCommon from 'src/components/house/ModalHouseCommon'
import TableWithBackground from 'src/components/house/TableWithBackground'

import EditButton from 'src/assets/img/house/edit_btn.svg'
import CancelButton from 'src/assets/img/house/btn_cancel.png'
import SaveButton from 'src/assets/img/house/btn_settle.png'

const MAX_USERNAME_LENGTH = 13
const MAX_MESSAGE_LENGTH = 40

const buildUserInfo = (user) => ({
  house_name: user?.house_name || user?.name || '',
  house_comment: user?.house_comment || '',
  house_active: user?.house_active !== undefined ? user.house_active : true,
  icon_id: user?.icon_id || null
})

const ModalUserProfile = ({ open, onClose, user, materialItems = [] }) => {
  const authUser = useAuthState().user
  const authDispatch = useAuthDispatch()

  const [isLoading, setIsLoading] = useState(false)
  const [userInfo, setUserInfo] = useState(() => buildUserInfo(user))
  const [avatarRepo, setAvatarRepo] = useState('he')

  useEffect(() => {
    if (open && user) {
      setUserInfo(buildUserInfo(user))
    }
  }, [open, user])

  const setField = (field, value) => setUserInfo((prev) => ({ ...prev, [field]: value }))

  const avatarList = useMemo(() => {
    if (!open || materialItems.length === 0) return []
    return materialItems
      .filter((item) => item.category === avatarRepo)
      .map((item) => ({ id: item.id, img: item.icon_url || '', label: item.name || 'Avatar' }))
  }, [open, materialItems, avatarRepo])

  const handleSubmit = async () => {
    const hasProhibitedWord = prohibitedWords.some((word) => userInfo.house_comment.includes(word))
    if (hasProhibitedWord || userInfo.house_name.length === 0 || !authUser?.id) return

    setIsLoading(true)
    try {
      const res = await patchUser(authUser.id, {
        house_name: userInfo.house_name.trim(),
        house_comment: userInfo.house_comment.trim(),
        house_active: userInfo.house_active,
        icon_id: userInfo.icon_id
      })
      if (res.success) {
        authDispatch({ type: authActionTypes.LOGIN_USER, payload: { ...authUser, ...res.items } })
        onClose()
      } else {
        console.error('Failed to update user:', res.error)
      }
    } catch (error) {
      console.error('Error updating user:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ModalHouseCommon
      open={open}
      onClose={onClose}
      height="max-h-[90%] max-w-[90%] xs:max-w-[350px]"
    >
      <div className="relative w-full h-full overflow-auto hidden-scroll flex flex-col font-ShinMaruGo">
        <div className="sticky top-0 z-20 flex w-full gap-2 border-b-[3px] rounded-tl-3xl border-house-pink bg-cream">
          <div className="py-2 rounded-tl-[16px] bg-house-pink bg-pat01">
            <span className="pl-4 pr-6 text-white text-lg tracking-wider font-semibold">
              プロフィール編集
            </span>
          </div>
        </div>

        <div className="w-full flex flex-col gap-4 justify-center pb-4 px-6 bg-pat02 flex-1">
          <div className="w-full">
            <div className="flex items-center gap-2 py-2">
              <span className="text-sm xs:text-base font-semibold tracking-wider">
                HOUSEの公開設定
              </span>
              <HousePublicButton
                isPublic={userInfo.house_active}
                setIsPublic={() => setField('house_active', !userInfo.house_active)}
              />
            </div>

            <div className="relative">
              <span className="absolute inset-0 rounded-[10px] -rotate-3 bg-[#694296B2]" />
              <span className="absolute inset-0 rounded-[10px] rotate-3 bg-[#F76B92B2]" />
              <TableWithBackground>
                <div className="flex flex-col gap-2 w-full">
                  <LabelModalHouse type="name" htmlFor="username" />
                  <div className="relative mx-3">
                    <input
                      type="text"
                      id="username"
                      name="username"
                      required
                      maxLength={MAX_USERNAME_LENGTH}
                      value={userInfo.house_name}
                      onChange={(e) => setField('house_name', e.target.value)}
                      className="w-full h-full pl-3 pr-10 text-xs xs:text-base font-medium text-house-black tracking-wider rounded-[10px] font-ShinMaruGo"
                    />
                    <label htmlFor="username" className="absolute top-1 right-2">
                      <img
                        src={EditButton}
                        alt="edit"
                        className="h-5 w-5 xs:h-6 xs:w-6 object-cover"
                      />
                    </label>
                  </div>
                </div>

                <div className="flex flex-col gap-1 w-full">
                  <LabelModalHouse type="message" htmlFor="message" />
                  <div className="flex flex-col gap-0 mx-3 py-1">
                    <textarea
                      id="message"
                      name="message"
                      rows={2}
                      maxLength={MAX_MESSAGE_LENGTH}
                      value={userInfo.house_comment}
                      onChange={(e) => setField('house_comment', e.target.value)}
                      placeholder="ようこそ！私のHOUSEへ♪25年ローンでようやく購入しました。"
                      className="resize-none w-full px-3 py-2 text-xs xs:text-base font-medium text-house-black tracking-wider rounded-t-[10px] font-ShinMaruGo"
                    />
                    <div className="flex gap-2 px-2 py-1 items-end justify-end bg-white rounded-b-[10px]">
                      <span className="text-xs xs:text-sm font-medium text-house-gray">
                        残り{MAX_MESSAGE_LENGTH - userInfo.house_comment.length}文字
                      </span>
                      <label htmlFor="message" className="h-5 w-5 xs:h-6 xs:w-6">
                        <img
                          src={EditButton}
                          alt="edit"
                          className="h-5 w-5 xs:h-6 xs:w-6 object-cover"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-1 w-full">
                  <LabelModalHouse type="rank" />
                  <div className="mx-3 pt-2 pb-4">
                    <LabelRank type={user?.ranking} />
                  </div>
                </div>
              </TableWithBackground>
            </div>
          </div>

          <div className="w-full">
            <TableWithBackground>
              <div className="flex gap-1 items-end justify-between">
                <LabelModalHouse type="icon" />
                <div className="flex">
                  <ButtonAvatarRepo
                    value="he"
                    label="彼"
                    selectedValue={avatarRepo}
                    onChange={setAvatarRepo}
                    rounded="left"
                  />
                  <ButtonAvatarRepo
                    value="myself"
                    label="私"
                    selectedValue={avatarRepo}
                    onChange={setAvatarRepo}
                    rounded="right"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2 overflow-auto">
                <AvatarGrid
                  avatars={avatarList}
                  selectedAvatarId={userInfo.icon_id}
                  onAvatarSelect={(id) => setField('icon_id', id)}
                />
              </div>
            </TableWithBackground>

            <div className="flex justify-around mt-4">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="cursor-pointer hover:scale-105 transition-transform focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <img src={CancelButton} alt="cancel" className="w-full" />
              </button>
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="cursor-pointer hover:scale-105 transition-transform focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <img src={SaveButton} alt="save" className="w-full" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalHouseCommon>
  )
}

ModalUserProfile.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  materialItems: PropTypes.array
}

export default ModalUserProfile
